import { useState } from 'react';
import { KeyRound } from 'lucide-react';

import { useAuth } from '@/lib/auth/AuthContext';
import { passkeysSupported, usePasskeySignIn } from '../api/passkeys';

interface Props {
  onError: (message: string) => void;
  onSuccess: () => void;
}

/**
 * Sign in with a passkey.
 *
 * Rendered only where WebAuthn exists. Unlike Apple, this *does* work on
 * localhost — the spec exempts it from the secure-context requirement precisely
 * so it can be developed against.
 *
 * Styled to match the social buttons rather than the primary Sign in button: it
 * is one of several ways in, not the default one.
 */
export function PasskeySignInButton({ onError, onSuccess }: Props) {
  const signIn = usePasskeySignIn();
  const { loginWithTokens } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!passkeysSupported()) return null;

  const handleClick = async () => {
    setBusy(true);
    onError('');
    try {
      const tokens = await signIn.mutateAsync();
      await loginWithTokens(tokens);
      onSuccess();
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

      // NotAllowedError is what the browser throws both when the user dismisses
      // the sheet and when it times out. Calling that a failure would be wrong
      // in the common case, which is simply changing your mind.
      if (name === 'NotAllowedError' || name === 'AbortError') {
        onError('');
      } else {
        onError(detail ?? 'That passkey did not work. Try another way to sign in.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      disabled={busy}
      className="flex h-10 w-full items-center justify-center gap-2 rounded-full border border-[rgb(var(--fgc)/0.18)] bg-transparent text-[14px] font-medium text-foreground transition-colors duration-200 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
      onClick={handleClick}>
      <KeyRound aria-hidden="true" className="size-[17px]" />
      {busy ? 'Waiting for your device…' : 'Sign in with a passkey'}
    </button>
  );
}
